import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';

const CM_PER_INCH = 2.54;
// Regions whose market conventionally uses inches -> selecting that region defaults the unit to
// inch (client: selecting "US" should switch cm -> inch). Extend if UK/AU ever need it.
// Regions whose charts open in inches instead of cm. Countries are matched on the region's country
// codes (Shopify uses GB for the United Kingdom); the aliases cover charts whose region has no
// country list and is identified only by its handle or title. Add a region by extending these.
const IMPERIAL_COUNTRIES = ['US', 'GB'];
const IMPERIAL_REGION_ALIASES = ['us', 'usa', 'uk', 'gb', 'united kingdom'];

class PdpSizeGuideConversion extends HTMLElement {
  #charts = [];
  #config = {};
  #strings = {};
  #state = {
    tab: 'product',
    regionTitle: '',
    unit: 'cm',
    regionDropdownOpen: false,
    activeRow: null,
    activeCol: null,
  };
  #delegate = null;
  #tabsEl = null;
  #regionDropdown = null;
  #regionTrigger = null;
  #regionValue = null;
  #regionList = null;
  #unitToggle = null;
  #tableWrap = null;
  #onDocumentClick = null;
  #onDocumentKeydown = null;
  #initialized = false;

  connectedCallback() {
    if (this.#initialized) return;

    this.#charts = this.#parseJson('[data-ref="charts"]') ?? [];
    this.#config = this.#parseJson('[data-ref="config"]') ?? {};
    this.#strings = this.#parseJson('[data-ref="strings"]') ?? {};

    if (!this.#charts.length) {
      this.closest('.pdp-size-guide__section--conversion')?.setAttribute('hidden', '');
      this.#initialized = true;
      return;
    }

    this.#tabsEl = this.querySelector('[data-ref="tabs"]');
    this.#regionDropdown = this.querySelector('[data-ref="region-dropdown"]');
    this.#regionTrigger = this.querySelector('[data-action="toggle-region-dropdown"]');
    this.#regionValue = this.querySelector('[data-ref="region-value"]');
    this.#regionList = this.querySelector('[data-ref="region-list"]');
    this.#unitToggle = this.querySelector('[data-ref="unit-toggle"]');
    this.#tableWrap = this.querySelector('[data-ref="table-wrap"]');

    this.#state.tab = this.#resolveInitialTab();
    this.#state.regionTitle = this.#resolveInitialRegion(this.#state.tab);
    // Open in the region's default unit (e.g. US region -> inch) instead of always cm.
    this.#state.unit = this.#defaultUnitForRegion(this.#state.regionTitle);

    this.#renderTabs();
    this.#renderRegionDropdown();
    this.#updateUnitToggle();
    this.#renderTable();
    this.#syncActiveTab();
    this.#dispatchTabChange();
    this.#bindEvents();
    this.#initialized = true;
  }

  disconnectedCallback() {
    if (this.closest('pdp-size-guide')?.hasAttribute('data-portaling')) return;

    this.#tearDown();
    this.#initialized = false;
  }

  #tearDown() {
    this.#delegate?.destroy?.();
    this.#delegate = null;

    if (this.#onDocumentClick) {
      document.removeEventListener('click', this.#onDocumentClick);
      this.#onDocumentClick = null;
    }

    if (this.#onDocumentKeydown) {
      document.removeEventListener('keydown', this.#onDocumentKeydown);
      this.#onDocumentKeydown = null;
    }
  }

  #parseJson(selector) {
    try {
      const el = this.querySelector(selector);
      if (!el?.textContent) return null;
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  #normalizeTab(tab) {
    return String(tab ?? '').trim().toLowerCase();
  }

  #chartsForTab(tab) {
    const normalized = this.#normalizeTab(tab);
    return this.#charts.filter((chart) => this.#normalizeTab(chart.tab) === normalized);
  }

  #availableTabs() {
    const tabs = new Set(this.#charts.map((chart) => this.#normalizeTab(chart.tab)).filter(Boolean));
    return Array.from(tabs);
  }

  #resolveInitialTab() {
    const preferred = this.#normalizeTab(this.dataset.defaultTab || 'product');
    const tabs = this.#availableTabs();
    if (tabs.includes(preferred)) return preferred;
    return tabs[0] || 'product';
  }

  #resolveInitialRegion(tab) {
    const charts = this.#chartsForTab(tab);
    if (!charts.length) return '';

    const countryCode = String(this.#config.countryCode ?? '').trim().toUpperCase();
    if (countryCode) {
      const geoMatch = charts.find((chart) =>
        (chart.region?.countries ?? []).some(
          (code) => String(code).trim().toUpperCase() === countryCode,
        ),
      );
      if (geoMatch?.region?.title) return geoMatch.region.title;
    }

    const sorted = [...charts].sort(
      (a, b) => (a.region?.sortOrder ?? 0) - (b.region?.sortOrder ?? 0),
    );
    return sorted[0]?.region?.title ?? '';
  }

  #activeChart() {
    return this.#chartsForTab(this.#state.tab).find(
      (chart) => chart.region?.title === this.#state.regionTitle,
    );
  }

  // Default measurement unit for a region: inch for imperial markets (US), cm otherwise. Matched by
  // the region's countries (robust) with a title/handle fallback. Fires only on region CHANGE +
  // initial load, so the shopper can still toggle cm/inch manually within a region afterwards.
  #defaultUnitForRegion(regionTitle) {
    const chart = this.#chartsForTab(this.#state.tab).find(
      (item) => item.region?.title === regionTitle,
    );
    const countries = (chart?.region?.countries ?? []).map((code) =>
      String(code).trim().toUpperCase(),
    );
    const handle = String(chart?.region?.handle ?? '').toLowerCase();
    const title = String(regionTitle ?? '').trim().toLowerCase();
    const isImperial =
      countries.some((code) => IMPERIAL_COUNTRIES.includes(code)) ||
      IMPERIAL_REGION_ALIASES.includes(handle) ||
      IMPERIAL_REGION_ALIASES.includes(title);
    return isImperial ? 'inch' : 'cm';
  }

  #regionOptionsForTab(tab) {
    const seen = new Set();
    const options = [];

    this.#chartsForTab(tab).forEach((chart) => {
      const title = chart.region?.title;
      if (!title || seen.has(title)) return;
      seen.add(title);
      options.push({
        title,
        sortOrder: chart.region?.sortOrder ?? 0,
      });
    });

    return options.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  #formatMeasurementValue(value) {
    const raw = String(value ?? '').trim();
    if (raw === '') return '';

    // Body-measurement cells can hold a range like "80-84" (the client uses ranges for body sizes).
    // Convert each end on its own so the cm<->inch toggle keeps the range correct (80-84 cm ->
    // 31.5-33.1 in) instead of Number("80-84") = NaN leaving the whole range unconverted under inch.
    const range = raw.match(/^(\d+(?:[.,]\d+)?)\s*[-\u2013]\s*(\d+(?:[.,]\d+)?)$/);
    if (range) {
      return `${this.#formatSingleMeasurement(range[1])}-${this.#formatSingleMeasurement(range[2])}`;
    }

    return this.#formatSingleMeasurement(raw);
  }

  #formatSingleMeasurement(value) {
    // Accept a comma decimal separator (e.g. "29,5") as well as a dot.
    const numeric = Number(String(value).replace(',', '.'));
    if (Number.isNaN(numeric)) return String(value ?? '');

    if (this.#state.unit === 'inch') {
      const inches = numeric / CM_PER_INCH;
      const rounded = Math.round(inches * 10) / 10;
      return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    }

    const roundedCm = Math.round(numeric * 10) / 10;
    return Number.isInteger(roundedCm) ? String(roundedCm) : roundedCm.toFixed(1);
  }

  #bindEvents() {
    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-action="set-tab"]', this.#onTabClick);
    this.#delegate.on('click', '[data-action="set-unit"]', this.#onUnitClick);
    this.#delegate.on('click', '[data-action="toggle-region-dropdown"]', this.#onToggleRegionDropdown);
    this.#delegate.on('click', '[data-action="select-region"]', this.#onSelectRegion);
    // Delegate listens in the bubbling phase, but mouseenter/mouseleave do NOT bubble, so a delegated
    // handler on the host never fires for the cells (desktop hover highlight silently did nothing).
    // mouseover/mouseout bubble; cells have no child elements so they behave like enter/leave here.
    this.#delegate.on('mouseover', '[data-action="highlight-col"]', this.#onColumnPointerEnter);
    this.#delegate.on('mouseout', '[data-ref="table-wrap"]', this.#onTableWrapPointerLeave);
    this.#delegate.on('click', '[data-action="highlight-col"]', this.#onColumnClick);

    this.#onDocumentClick = (event) => {
      if (this.#state.regionDropdownOpen && !this.#regionDropdown?.contains(event.target)) {
        this.#setRegionDropdownOpen(false);
      }

      if (
        (this.#state.activeRow != null || this.#state.activeCol != null) &&
        !this.#tableWrap?.contains(event.target)
      ) {
        this.#clearActiveCell();
      }
    };

    this.#onDocumentKeydown = (event) => {
      if (event.key === 'Escape' && this.#state.regionDropdownOpen) {
        this.#setRegionDropdownOpen(false);
        this.#regionTrigger?.focus();
      }
    };

    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);
  }

  #onTabClick = (event, target) => {
    const tab = target.dataset.tab;
    if (!tab || tab === this.#state.tab) return;

    const previousRegion = this.#state.regionTitle;
    this.#state.tab = tab;
    this.#setRegionDropdownOpen(false);

    const options = this.#regionOptionsForTab(tab);
    const stillValid = options.some((option) => option.title === previousRegion);
    this.#state.regionTitle = stillValid ? previousRegion : this.#resolveInitialRegion(tab);
    // If switching tabs lands on a different region, follow its default unit too.
    if (!stillValid) {
      this.#state.unit = this.#defaultUnitForRegion(this.#state.regionTitle);
    }

    this.#state.activeRow = null;
    this.#state.activeCol = null;
    this.#renderTabs();
    this.#renderRegionDropdown();
    this.#updateUnitToggle();
    this.#renderTable();
    this.#syncActiveTab();
    this.#dispatchTabChange();
  };

  #syncActiveTab() {
    this.dataset.activeTab = this.#state.tab;
  }

  #onColumnPointerEnter = (_event, target) => {
    if (!responsive.matchesBreakpoint('lap_and_up')) return;
    this.#setActiveCellFromTarget(target);
  };

  #onTableWrapPointerLeave = (event) => {
    if (!responsive.matchesBreakpoint('lap_and_up')) return;

    const next = event.relatedTarget;
    if (next && this.#tableWrap?.contains(next)) return;

    this.#clearActiveCell();
  };

  #onColumnClick = (event, target) => {
    if (responsive.matchesBreakpoint('lap_and_up')) return;

    event.preventDefault();
    const col = target.dataset.col;
    if (!col || col === '0') return;

    if (target.closest('thead')) {
      if (this.#state.activeCol === col && this.#state.activeRow == null) {
        this.#clearActiveCell();
      } else {
        this.#setActiveColumnOnly(col);
      }
      return;
    }

    const row = target.closest('tr')?.dataset.row;
    if (row == null) return;

    if (this.#state.activeRow === row && this.#state.activeCol === col) {
      this.#clearActiveCell();
      return;
    }

    this.#setActiveCell(row, col);
  };

  #setActiveCellFromTarget(target) {
    const col = target.dataset.col;
    if (!col || col === '0') return;

    if (target.closest('thead')) {
      this.#setActiveColumnOnly(col);
      return;
    }

    const row = target.closest('tr')?.dataset.row;
    if (row == null) return;
    this.#setActiveCell(row, col);
  }

  #setActiveCell(row, col) {
    this.#state.activeRow = String(row);
    this.#state.activeCol = String(col);
    this.#applyActiveCellHighlight();
  }

  #setActiveColumnOnly(col) {
    this.#state.activeRow = null;
    this.#state.activeCol = String(col);
    this.#applyActiveCellHighlight();
  }

  #clearActiveCell() {
    this.#state.activeRow = null;
    this.#state.activeCol = null;
    this.#applyActiveCellHighlight();
  }

  #applyActiveCellHighlight() {
    const table = this.#tableWrap?.querySelector('.pdp-size-guide-conversion__table');
    if (!table) return;

    table.querySelectorAll('.is-row-highlighted, .is-col-highlighted').forEach((cell) => {
      cell.classList.remove('is-row-highlighted', 'is-col-highlighted');
    });

    const { activeRow, activeCol } = this.#state;
    if (activeCol == null) {
      delete table.dataset.activeRow;
      delete table.dataset.activeCol;
      return;
    }

    table.dataset.activeCol = activeCol;

    if (activeRow == null) {
      delete table.dataset.activeRow;
    } else {
      table.dataset.activeRow = activeRow;
      table
        .querySelectorAll(`tbody tr[data-row="${activeRow}"] .pdp-size-guide-conversion__cell`)
        .forEach((cell) => {
          cell.classList.add('is-row-highlighted');
        });
    }

    table.querySelectorAll(`[data-col="${activeCol}"]`).forEach((cell) => {
      cell.classList.add('is-col-highlighted');
    });
  }

  #onToggleRegionDropdown = (event) => {
    event.preventDefault();
    this.#setRegionDropdownOpen(!this.#state.regionDropdownOpen);
  };

  #onSelectRegion = (event, target) => {
    const value = target.dataset.value;
    if (!value || value === this.#state.regionTitle) {
      this.#setRegionDropdownOpen(false);
      return;
    }

    this.#state.regionTitle = value;
    // Selecting an imperial region (US) switches the unit to inch; metric regions to cm.
    this.#state.unit = this.#defaultUnitForRegion(value);
    this.#setRegionDropdownOpen(false);
    this.#state.activeRow = null;
    this.#state.activeCol = null;
    this.#renderRegionDropdown();
    this.#updateUnitToggle();
    this.#renderTable();
  };

  #onUnitClick = (event, target) => {
    const unit = target.dataset.unit;
    if (!unit || unit === this.#state.unit) return;

    this.#state.unit = unit;
    this.#state.activeRow = null;
    this.#state.activeCol = null;
    this.#updateUnitToggle();
    this.#renderTable();
  };

  #setRegionDropdownOpen(isOpen) {
    this.#state.regionDropdownOpen = isOpen;
    this.#regionTrigger?.setAttribute('aria-expanded', String(isOpen));
    this.#regionDropdown?.classList.toggle('is-open', isOpen);

    if (this.#regionList) {
      this.#regionList.hidden = !isOpen;
    }
  }

  #dispatchTabChange() {
    this.dispatchEvent(
      new CustomEvent('size-guide:tab-change', {
        bubbles: true,
        detail: { tab: this.#state.tab },
      }),
    );
  }

  #renderTabs() {
    if (!this.#tabsEl) return;

    const tabs = this.#availableTabs();
    if (tabs.length < 2) {
      this.#tabsEl.hidden = true;
      this.#tabsEl.innerHTML = '';
      return;
    }

    this.#tabsEl.hidden = false;
    this.#tabsEl.innerHTML = tabs
      .map((tab) => {
        const label = tab === 'body' ? this.#strings.tabBody : this.#strings.tabProduct;
        const isActive = tab === this.#state.tab;
        return `<button
          type="button"
          class="pdp-size-guide-conversion__tab u-p3${isActive ? ' is-active' : ''}"
          data-action="set-tab"
          data-tab="${tab}"
          aria-pressed="${isActive}"
        >${label}</button>`;
      })
      .join('');
  }

  #renderRegionDropdown() {
    const options = this.#regionOptionsForTab(this.#state.tab);

    if (!options.some((option) => option.title === this.#state.regionTitle)) {
      this.#state.regionTitle = options[0]?.title ?? '';
    }

    if (this.#regionValue) {
      this.#regionValue.textContent = this.#state.regionTitle;
    }

    if (!this.#regionList) return;

    this.#regionList.innerHTML = options
      .map((option) => {
        const isActive = option.title === this.#state.regionTitle;
        return `<button
          type="button"
          class="pdp-size-guide-conversion__region-option u-p3${isActive ? ' is-active' : ''}"
          data-action="select-region"
          data-value="${this.#escapeAttr(option.title)}"
          role="option"
          aria-selected="${isActive}"
        >${this.#escapeHtml(option.title)}</button>`;
      })
      .join('');
  }

  #updateUnitToggle() {
    if (!this.#unitToggle) return;

    this.#unitToggle.querySelectorAll('[data-unit]').forEach((button) => {
      const isActive = button.dataset.unit === this.#state.unit;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-checked', String(isActive));
    });
  }

  #renderTable() {
    if (!this.#tableWrap) return;

    const chart = this.#activeChart();
    if (!chart) {
      this.#tableWrap.innerHTML = '';
      return;
    }

    const columns = chart.sizeColumns ?? [];
    const regionSizes = chart.regionSizes ?? [];
    const measurements = chart.measurements ?? [];
    const rowLabel = chart.region?.rowLabel || chart.region?.title || '';

    const headCells = columns
      .map(
        (col, index) =>
          `<th class="pdp-size-guide-conversion__cell u-p3" scope="col" data-col="${index + 1}" data-action="highlight-col">${this.#escapeHtml(col)}</th>`,
      )
      .join('');

    const regionCells = regionSizes
      .map(
        (size, index) =>
          `<td class="pdp-size-guide-conversion__cell u-p3" data-col="${index + 1}" data-action="highlight-col">${this.#escapeHtml(size)}</td>`,
      )
      .join('');

    const measurementRows = measurements
      .map((row, index) => {
        const values = (row.valuesCm ?? [])
          .map(
            (value, colIndex) =>
              `<td class="pdp-size-guide-conversion__cell u-p3" data-col="${colIndex + 1}" data-action="highlight-col">${this.#escapeHtml(this.#formatMeasurementValue(value))}</td>`,
          )
          .join('');
        const rowClass =
          index % 2 === 0
            ? 'pdp-size-guide-conversion__row'
            : 'pdp-size-guide-conversion__row pdp-size-guide-conversion__row--alt';
        return `<tr class="${rowClass}" data-row="${index + 1}">
          <th class="pdp-size-guide-conversion__cell pdp-size-guide-conversion__cell--label u-p3" scope="row" data-col="0">${this.#escapeHtml(row.label)}</th>
          ${values}
        </tr>`;
      })
      .join('');

    this.#tableWrap.innerHTML = `<table class="pdp-size-guide-conversion__table">
      <thead>
        <tr class="pdp-size-guide-conversion__row">
          <th class="pdp-size-guide-conversion__cell pdp-size-guide-conversion__cell--label u-p3" scope="col" data-col="0">${this.#escapeHtml(this.#strings.almadaSize)}</th>
          ${headCells}
        </tr>
      </thead>
      <tbody>
        <tr class="pdp-size-guide-conversion__row pdp-size-guide-conversion__row--alt" data-row="0">
          <th class="pdp-size-guide-conversion__cell pdp-size-guide-conversion__cell--label u-p3" scope="row" data-col="0">${this.#escapeHtml(rowLabel)}</th>
          ${regionCells}
        </tr>
        ${measurementRows}
      </tbody>
    </table>`;

    this.#applyActiveCellHighlight();
  }

  #escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  #escapeAttr(value) {
    return this.#escapeHtml(value).replace(/'/g, '&#39;');
  }
}

if (!customElements.get('pdp-size-guide-conversion')) {
  customElements.define('pdp-size-guide-conversion', PdpSizeGuideConversion);
}
